import { useEffect, useRef, useState } from 'react';
import { ApiError } from '@/api/client';
import { useT } from '@/i18n/runtime';
import { capturePan, electPanOfficerVerify, listPanAttempts, PAN_PATTERN } from '@/api/pan';
import type { PanVerificationResultDTO } from '@/api/pan';
import { useSessionStore } from '@/store/sessionStore';
import type { StepProps } from '../types';
import { AppBar } from '../components/AppBar';
import { Button } from '../components/Button';
import { SecurePill } from '../components/SecurePill';
import { Stepper } from '../components/Stepper';
import { useCheckStepFooter } from './useCheckStepFooter';

/**
 * PAN_SOURCE as a customer PRE-CALL step (owner item 3: "PAN fetch as a precheck like DigiLocker
 * or in-call as current").
 *
 * The backend has admitted this all along — PAN_SOURCE's legal phases are {PRE_CALL, IN_CALL} with
 * no phase lock, and `POST /sessions/{id}/pan` is policy-gated but not state-banded. The only block
 * was this screen not existing: the droom's customer-facing set excluded PAN_SOURCE as
 * "agent-driven", which made the tenant's PRE_CALL pin unreachable. Whether the check runs here or
 * on the call stays a TENANT decision (the frozen plan's phase) — this component renders only when
 * the plan pins it PRE_CALL; an in-call pin keeps today's flow untouched.
 *
 * EVIDENCE, NEVER A VERDICT — the honesty line this screen must hold: the source check's outcome is
 * an ADVISORY the verification officer confirms on the call. The success copy says "checked against
 * the issuing source · your officer will confirm on the call", and a NAME_MISMATCH renders as
 * something to fix or hand to the officer — never as a rejection. The customer is never dead-ended:
 * REQUIRED + provider trouble offers the honest continue-with-officer fallback (the check simply
 * runs in-call, exactly as it always has).
 */
export function PanStep({ tenant }: StepProps): JSX.Element {
  const { t } = useT();
  const sessionId = useSessionStore((s) => s.session.sessionId) as string;
  const footer = useCheckStepFooter('PAN_SOURCE');

  const [pan, setPan] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<PanVerificationResultDTO | null>(null);
  const [failure, setFailure] = useState<'provider' | 'format' | null>(null);
  const attempts = useRef(0);

  /**
   * Testmap #11 (20-Aug live, both tenants): POST /pan was OFFICER-only on the deployed be — every
   * customer submit answered 403 (AUTHZ_DENIED) and the step dead-ended as "PAN doesn't work". The
   * be lane's fix (be `281cb75`) makes POST /pan a two-door route (officer sessions.write +
   * customer media.join, own-session, consent-first, pre-live band). The landed contract this step
   * is CAPABILITY-AWARE against:
   *
   *   • `GET /pan` (the attempts read) STAYS officer-plane (`sessions.read`) — a customer 403
   *     there is EXPECTED on every be, old and new, and carries NO information about the capture
   *     door. It must never hide the form.
   *   • The capture door itself is the only honest capability signal: a 403 on the POST means this
   *     deployment refuses the customer leg — the form is withdrawn for the honest absence card
   *     ("your officer checks this with you on the call"), whose Continue records the audited
   *     officer-verify election (`/pan/fallback:elect`, the customer leg every be serves) so the
   *     CF8 queue gate admits the session.
   */
  const [capability, setCapability] = useState<'served' | 'refused'>('served');
  const [electing, setElecting] = useState(false);

  // Resume support: a prior VALID attempt satisfies the step without re-asking for the PAN —
  // re-entering evidence that is already on the ladder would only manufacture duplicate attempts.
  // (Officer-plane read: for a customer principal this 403s on every be — swallowed, the form is
  // the honest default; see the capability note above.)
  useEffect(() => {
    const ctrl = new AbortController();
    listPanAttempts(sessionId, ctrl.signal)
      .then((rows) => {
        if (rows.some((r) => r.verdict === 'VALID')) footer.satisfy();
      })
      .catch(() => {
        /* no prior attempts / read unavailable / officer-plane 403 — the form is the honest default */
      });
    return () => ctrl.abort();
    // footer.satisfy is stable per store contract; run once per session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // The honest continue off the refused wire: record the officer-verify election server-side
  // (best-effort — the waiting room's 422 refusal card remains the backstop if the election
  // itself is refused), then advance via the fallback guard: the check runs on the call.
  const continueWithOfficer = async () => {
    if (electing) return;
    setElecting(true);
    try {
      await electPanOfficerVerify(sessionId);
    } catch {
      /* the election is best-effort; queue:enter names anything still owed */
    } finally {
      footer.fallback();
    }
  };

  const panClean = pan.toUpperCase().replace(/\s/g, '');
  const panShapeOk = PAN_PATTERN.test(panClean);
  const submittable = panShapeOk && name.trim().length >= 2 && !busy;

  const submit = async () => {
    if (!submittable) {
      if (!panShapeOk) setFailure('format');
      return;
    }
    setBusy(true);
    setFailure(null);
    try {
      const out = await capturePan(sessionId, { pan: panClean, nameAsPerDocument: name.trim() });
      attempts.current += 1;
      setResult(out);
      if (out.verdict === 'ERROR') setFailure('provider');
    } catch (e) {
      attempts.current += 1;
      if (e instanceof ApiError && e.status === 403) {
        // #11: the capture door refused the customer principal — this deployment has no customer
        // leg. Withdraw the form for the honest absence card (never a form that can only 403).
        setCapability('refused');
        return;
      }
      if (e instanceof ApiError && e.status === 404) {
        // PAN is OFF in this session's pinned policy — the plan and the pin disagree (a config skew,
        // not the customer's problem). Continue without it rather than dead-ending.
        footer.isRequired ? footer.fallback() : footer.skip();
        return;
      }
      setFailure('provider');
      setResult(null);
    } finally {
      setBusy(false);
    }
  };

  const verdict = result?.verdict ?? null;

  return (
    <div className="app-view">
      <AppBar tenant={tenant} subtitle={t('droom.pan.subtitle')} right={<SecurePill kind="secure" label={t('droom.pill.encrypted')} />} />
      <Stepper current="check:PAN_SOURCE" />

      <div className="stack" style={{ marginTop: 12 }}>
        <div className="h-title sm">{t('droom.pan.title')}</div>
        <p className="sub">{t('droom.pan.sub')}</p>

        {capability === 'refused' && verdict !== 'VALID' ? (
          /* #11 — the honest absence. The wire refuses customer PAN capture on this deployment,
             so no form is offered (a step the wire refuses must never render); the check simply
             runs with the officer on the call, which is exactly what always worked. */
          <section className="card stack" style={{ padding: 16 }} aria-live="polite">
            <div className="h-title sm">{t('droom.pan.oncall.title')}</div>
            <p className="sub">{t('droom.pan.oncall.body')}</p>
            <Button onClick={() => void continueWithOfficer()} disabled={electing}>
              {electing ? t('droom.pan.checking') : t('droom.action.continue')}
            </Button>
          </section>
        ) : verdict === 'VALID' ? (
          <section className="card stack" style={{ padding: 16 }} aria-live="polite">
            <div className="h-title sm">{t('droom.pan.checked.title')}</div>
            <p className="sub">
              {result?.panMasked
                ? t('droom.pan.checked.sub.masked', { masked: result.panMasked })
                : t('droom.pan.checked.sub')}
            </p>
            {result?.certification ? (
              <p className="sub mono" style={{ fontSize: 11 }}>
                {result.certification}
              </p>
            ) : null}
            <Button onClick={() => footer.satisfy()}>{t('droom.action.continue')}</Button>
          </section>
        ) : (
          <section className="card stack" style={{ padding: 16 }}>
            {/* .field is the droom's stacked-field primitive (label above a full-width control).
                Bare <label>/<input> are inline boxes with no reset in this app's CSS, so without
                it the two labels and two inputs share ONE inline formatting context and the copy
                wraps as prose around the boxes. htmlFor/id keeps the association explicit. */}
            <div className="field">
              <label className="field-label" htmlFor="pan-input">
                {t('droom.pan.label.pan')}
              </label>
              <input
                id="pan-input"
                className="field-input field-input-code"
                type="text"
                inputMode="text"
                autoCapitalize="characters"
                autoComplete="off"
                maxLength={10}
                placeholder="AAAAA9999A"
                value={pan}
                onChange={(e) => {
                  setPan(e.target.value.toUpperCase());
                  setFailure(null);
                }}
                aria-invalid={failure === 'format'}
                aria-describedby={failure === 'format' ? 'pan-format-error' : undefined}
              />
            </div>
            <div className="field">
              <label className="field-label" htmlFor="pan-name-input">
                {t('droom.pan.label.name')}
              </label>
              <input
                id="pan-name-input"
                className="field-input"
                type="text"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            {failure === 'format' ? (
              <p className="field-error" id="pan-format-error" role="alert">
                {t('droom.pan.err.format')}
              </p>
            ) : null}
            {verdict === 'NAME_MISMATCH' ? (
              <p className="sub" role="alert">
                {typeof result?.nameMatchScore === 'number'
                  ? t('droom.pan.err.mismatch.score', {
                      score: (result.nameMatchScore * 100).toFixed(0),
                    })
                  : t('droom.pan.err.mismatch')}
              </p>
            ) : null}
            {verdict === 'INVALID' || verdict === 'NOT_FOUND' ? (
              <p className="sub" role="alert">
                {t('droom.pan.err.invalid')}
              </p>
            ) : null}
            {failure === 'provider' ? (
              <p className="sub" role="alert">
                {t('droom.pan.err.provider')}
              </p>
            ) : null}

            <Button onClick={() => void submit()} disabled={!submittable}>
              {busy ? t('droom.pan.checking') : t('droom.pan.check')}
            </Button>

            {/* The honest continues. OPTIONAL: plain skip. REQUIRED: continue-with-officer appears
                only after a failed attempt or provider trouble — never as a first-resort bypass
                (that would quietly turn every PRE_CALL pin back into in-call). */}
            {footer.isOptional ? (
              <Button variant="ghost" onClick={() => footer.skip()} type="button">
                {t('droom.pan.skip')}
              </Button>
            ) : null}
            {footer.isRequired && (attempts.current > 0 || failure === 'provider') ? (
              <Button variant="ghost" onClick={() => footer.fallback()} type="button">
                {t('droom.pan.fallback')}
              </Button>
            ) : null}
          </section>
        )}

        <p className="sub" style={{ fontSize: 12 }}>
          {t('droom.pan.privacy')}
        </p>
      </div>
    </div>
  );
}
